<script setup>
import { storeToRefs } from "pinia";
import { computed, onMounted, ref } from "vue";

import { ArtHandler } from "@/handlers/artHandler";
import { UserHandler } from "@/handlers/userHandler";
import { useAppStore } from "@/stores";
import { toWeekdayDateString } from "@/utils/dateFormat";
import { daysUntil, expiryText } from "@/utils/dates";
import { capitalizeFirstLetter } from "@/utils/text";

const store = useAppStore();
const { user, license, licenseUsedCount } = storeToRefs(store);
const expireDays = () => daysUntil(license.value.expiryDate);

const artsCount = ref(null);
const membersCount = ref(null);

onMounted(async () => {
  const [arts, users] = await Promise.allSettled([
    new ArtHandler().getArts(),
    new UserHandler().getUsers(),
  ]);
  if (arts.status === "fulfilled") {
    artsCount.value = (arts.value.data || []).length;
  }
  if (users.status === "fulfilled") {
    const rsp = users.value;
    membersCount.value = rsp.pagination?.total ?? rsp.data?.length ?? 0;
  }
});

const greeting = computed(() => {
  const name = user.value?.name?.split(" ")[0] || "";
  return name ? `Hi ${name}` : "Welcome back";
});

const today = computed(() => toWeekdayDateString(new Date()));

const teamsUsed = computed(() => licenseUsedCount.value);
const teamsTotal = computed(() => license.value?.numberOfTeams ?? null);
const showTeamsKpi = computed(
  () => teamsUsed.value !== null && teamsTotal.value !== null && teamsTotal.value > 0,
);

const planName = computed(() =>
  license.value?.plan ? capitalizeFirstLetter(license.value.plan) : null,
);

const artsTile = computed(() =>
  artsCount.value === null
    ? null
    : {
        label: "ARTs",
        value: `${artsCount.value}`,
        meta: "total",
        tone: "neutral",
      },
);

const membersTile = computed(() =>
  membersCount.value === null
    ? null
    : {
        label: "Users",
        value: `${membersCount.value}`,
        meta: "registered",
        tone: "neutral",
      },
);

const teamsTile = computed(() => ({
  label: "Teams",
  value: `${teamsUsed.value}`,
  meta: `of ${teamsTotal.value}`,
  tone: "neutral",
}));

function planTile() {
  if (!planName.value) return null;
  const days = expireDays();
  return {
    label: "Plan",
    value: planName.value,
    ...(days === null
      ? { meta: "Active", tone: "neutral" }
      : {
          meta: expiryText(days),
          tone: days <= 0 ? "alarm" : days < 30 ? "warning" : "neutral",
        }),
  };
}

function tiles() {
  return [
    artsTile.value,
    showTeamsKpi.value ? teamsTile.value : null,
    membersTile.value,
    planTile(),
  ].filter(Boolean);
}
</script>

<template>
  <header class="home-header">
    <div class="greeting">
      <h1 class="greeting-title">{{ greeting }}</h1>
      <p class="greeting-meta">
        {{ today }}
      </p>
    </div>

    <div class="kpis">
      <div v-for="tile in tiles()" :key="tile.label" class="kpi" :class="`kpi-${tile.tone}`">
        <span class="kpi-label">{{ tile.label }}</span>
        <span class="kpi-value">
          {{ tile.value }}
        </span>
        <span class="kpi-meta">{{ tile.meta }}</span>
      </div>
    </div>
  </header>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/mixins/typography";

.home-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 8px 4px 16px;
}

.greeting {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.greeting-title {
  @include typography.heading;

  color: c.$main;
  margin: 0;
}

.greeting-meta {
  @include typography.small;

  color: c.$text-subtle;
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.kpis {
  display: flex;
  align-items: stretch;
  gap: 20px;
}

.kpi {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 14px;
  border-left: 1px solid c.$border-light;
  min-width: 92px;
}

.kpi:first-child {
  border-left: none;
}

.kpi-label {
  @include typography.caption("semi-bold");

  color: c.$text-subtle;
  text-transform: uppercase;
}

.kpi-value {
  @include typography.large("bold");

  color: c.$main;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.kpi-alarm .kpi-value {
  color: c.$destructive;
}

.kpi-warning .kpi-value {
  color: c.$orange;
}

.kpi-okay .kpi-value {
  color: c.$main;
}

.kpi-meta {
  @include typography.caption;

  color: c.$text-subtle;
  min-height: 14px;
}

.kpi-alarm .kpi-meta {
  @include typography.caption("medium");

  color: c.$destructive;
}

@media (width < 880px) {
  .home-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .kpis {
    width: 100%;
    overflow-x: auto;
  }
}
</style>
